import React, { useRef, useState, useEffect, useMemo } from 'react';
import { processRoute } from '../utils/routeProcessing';
import { createLibrary } from '../data/routeLibrary';
import {
    clamp01,
    distPct,
    distToSegment,
    ydDistAbs,
    absPathLengthYds,
    absPositionAtArclen,
    pointsToPath,
    shiftPolyline,
    uAlongPolylineClosest,
    getPlayerPosAtTime,
    computeHandoffTime,
    THROW_SPEEDS_YPS,
    getDynamicBallSegments,
    anchoredPolylineFor,
    splitPolylineAtU,
    findUOnPolyline
} from '../utils/ballLogic';

/* ---------- Field constants (percent space with yard-correct lengths) ---------- */
const FIELD_YARDS_W = 53.3;
const FIELD_YARDS_H = 100.0;
const unitX_to_yd = FIELD_YARDS_W / 100.0;
const unitY_to_yd = FIELD_YARDS_H / 100.0;

const SNAP_SPEED_YPS = 18;
const MIN_SNAP_DURATION = 0.05;
const MAX_SNAP_DURATION = 0.4;

const PLAY_DURATION = 10.0;
const HANDOFF_RADIUS_YDS = 8.0;



/* ==== FOOTBALL ICON + SPIRAL (DROP-IN BLOCK) ==== */
function FootballIcon({ scale = 1, lacesPhase = 0 }) {
    const lacesX = Math.sin(lacesPhase) * 3;
    const lacesScaleX = Math.cos(lacesPhase);
    const showLaces = lacesScaleX > -0.3;

    return (
        <g transform={`scale(${scale})`}>
            <path
                d="M 0 -8 C 8 -5, 8 5, 0 8 C -8 5, -8 -5, 0 -8 Z"
                fill="#92400e"
                stroke="#3f1d0f"
                strokeWidth="2"
            />
            <line x1="0" y1="-7" x2="0" y2="7" stroke="#3f1d0f" strokeWidth="0.5" opacity="0.5" />
            {showLaces && (
                <g transform={`translate(${lacesX}, 0) scale(${Math.abs(lacesScaleX)}, 1)`}>
                    <rect x="-0.8" y="-3" width="1.6" height="6" rx="0.4" fill="#fff" opacity="0.95" />
                    <line x1="-2.5" y1="-2" x2="2.5" y2="-2" stroke="#fff" strokeWidth="1" />
                    <line x1="-2.5" y1="-0.7" x2="2.5" y2="-0.7" stroke="#fff" strokeWidth="1" />
                    <line x1="-2.5" y1="0.7" x2="2.5" y2="0.7" stroke="#fff" strokeWidth="1" />
                    <line x1="-2.5" y1="2" x2="2.5" y2="2" stroke="#fff" strokeWidth="1" />
                </g>
            )}
        </g>
    );
}

function HandIcon({ scale = 1 }) {
    return (
        <g transform={`scale(${scale}) translate(-12,-12)`}>
            {/* Soft Shadow Backdrop */}
            <circle cx="12" cy="12" r="10.5" fill="rgba(0,0,0,0.5)" />

            {/* Premium Metallic Ring */}
            <circle cx="12" cy="12" r="9.5" fill="#0f172a" stroke="#fbbf24" strokeWidth="1" />

            {/* Master UX FULL OPEN HAND: Clearer 5-finger palm */}
            <g transform="translate(12, 12)">
                <path
                    d="M-4,1 C-4.5,1 -5,0.5 -5,0 L-5,-4 C-5,-4.5 -4.5,-5 -4,-5 C-3.5,-5 -3,-4.5 -3,-4 L-3,-1 L-2,-1 L-2,-6 C-2,-6.5 -1.5,-7 -1,-7 C-0.5,-7 0,-6.5 0,-6 L0,-1 L1,-1 L1,-5.5 C1,-6 1.5,-6.5 2,-6.5 C2.5,-6.5 3,-6 3,-5.5 L3,-1 L4,-1 L4,-3.5 C4,-4 4.5,-4.5 5,-4.5 C5.5,-4.5 6,-4 6,-3.5 L6,2 C6,5 4,7 1,7 L-1,7 C-3,7 -5,5 -5,2 L-5,0"
                    fill="#fbbf24"
                    transform="scale(0.8)"
                />
            </g>

            {/* Subtle Inner Glow */}
            <circle cx="12" cy="12" r="9.5" fill="none" stroke="white" strokeWidth="0.2" opacity="0.2" />
        </g>
    );
}

function ReticleIcon({ scale = 1 }) {
    return (
        <g transform={`scale(${scale}) translate(-12,-12)`}>
            {/* Soft Shadow Backdrop */}
            <circle cx="12" cy="12" r="10.5" fill="rgba(0,0,0,0.5)" />

            {/* Premium Metallic Ring */}
            <circle cx="12" cy="12" r="9.5" fill="#0f172a" stroke="currentColor" strokeWidth="0.8" />

            {/* Inner HUD elements */}
            <g transform="translate(12, 12)">
                <circle cx="0" cy="0" r="6" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="1,2" opacity="0.4" />
                <path d="M-8 0 L-5 0 M8 0 L5 0 M0 -8 L0 -5 M0 8 L0 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                <circle cx="0" cy="0" r="1.5" fill="currentColor" />
            </g>
        </g>
    );
}

function HandMagicIcon({ scale = 1 }) {
    return (
        <g transform={`scale(${scale}) translate(-12,-12)`}>
            {/* Bat-signal-like backglow */}
            <circle cx="12" cy="12" r="14" fill="url(#magicGlow)" opacity="0.6">
                <animate attributeName="r" values="10;16;12" dur="0.3s" repeatCount="1" />
                <animate attributeName="opacity" values="0.8;0.2;0" dur="0.3s" repeatCount="1" />
            </circle>
            <defs>
                <radialGradient id="magicGlow">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="100%" stopColor="transparent" />
                </radialGradient>
            </defs>
            {/* Stylized Magic Hand */}
            <g transform="translate(12, 12) scale(0.7)">
                <path
                    d="M-4,1 C-4.5,1 -5,0.5 -5,0 L-5,-4 C-5,-4.5 -4.5,-5 -4,-5 C-3.5,-5 -3,-4.5 -3,-4 L-3,-1 L-2,-1 L-2,-6 C-2,-6.5 -1.5,-7 -1,-7 C-0.5,-7 0,-6.5 0,-6 L0,-1 L1,-1 L1,-5.5 C1,-6 1.5,-6.5 2,-6.5 C2.5,-6.5 3,-6 3,-5.5 L3,-1 L4,-1 L4,-3.5 C4,-4 4.5,-4.5 5,-4.5 C5.5,-4.5 6,-4 6,-3.5 L6,2 C6,5 4,7 1,7 L-1,7 C-3,7 -5,5 -5,2 L-5,0"
                    fill="#fbbf24"
                    stroke="#b45309"
                    strokeWidth="1"
                />
                <path d="M-2,-2 L2,2 M-2,2 L2,-2" stroke="#fff" strokeWidth="0.5" opacity="0.8">
                    <animate attributeName="opacity" values="1;0" dur="0.3s" repeatCount="1" />
                </path>
            </g>
        </g>
    );
}

function computeBallAngles({ prevPos, currPos, dt, spinRps, spinClock }) {
    let headingDeg = 0;
    if (prevPos && currPos) {
        const vx = (currPos.x - prevPos.x) / Math.max(dt, 1e-6);
        const vy = (currPos.y - prevPos.y) / Math.max(dt, 1e-6);
        const speed2 = vx * vx + vy * vy;
        if (speed2 > 0.001) headingDeg = (Math.atan2(vy, vx) * 180) / Math.PI;
    }
    const spinDeg = (spinClock * spinRps * 360) % 360;
    return { headingDeg, spinDeg };
}

function AutoBallSpiral({ x, y, isPlaying, scale = 1, overrideHeading = null, spinRps = 3 }) {
    const ballPrevRef = useRef(null);
    const ballSpinClockRef = useRef(0);
    const ballLastTimeRef = useRef(performance.now());

    const now = performance.now();
    const dt = (now - ballLastTimeRef.current) / 1000;
    ballLastTimeRef.current = now;

    const currPos = { x, y };
    const prevPos = ballPrevRef.current;

    if (isPlaying) ballSpinClockRef.current += dt;

    const { headingDeg } = computeBallAngles({
        prevPos,
        currPos,
        dt,
        spinRps,
        spinClock: ballSpinClockRef.current,
    });

    ballPrevRef.current = currPos;

    const finalHeading = overrideHeading !== null && overrideHeading !== undefined ? overrideHeading : (isPlaying ? headingDeg : 0);
    const lacesPhase = ballSpinClockRef.current * spinRps * 2 * Math.PI;

    return (
        <g transform={`rotate(${finalHeading})`}>
            <FootballIcon scale={scale} lacesPhase={isPlaying ? lacesPhase : 0} />
        </g>
    );
}

/* ---------- Component ---------- */
const Field = ({
    players = [],
    setPlayers = () => { },
    selectedTool = 'select',
    routes = [],
    setRoutes = () => { },
    drawingSettings = {},
    setDrawingSettings = () => { },
    selectedPlayerId = null,
    setSelectedPlayerId = () => { },
    selectedPlayerIds = new Set(),
    setSelectedPlayerIds = () => { },
    globalSettings = {},
    onContextMenu = () => { },
    playbackState = { isPlaying: false, progress: 0 },
    ballSpec = { enabled: false },
    setBallSpec = () => { },
    ballWizardStep = 0,
    setBallWizardStep = () => { },
    isLocked,
    // Global Settings
    iconSize = 1.0,
    showRoutes = true,
    showGridlines = true,
    autoProcessOptions = {},
    viewMode = 'portrait', // Default
    straightLineMode = false,
    onBeginChange,
    // NEW: Profile Props
    profileConfig,      // { throwProfileKey, ... }
    labelMode = 'simple', // 'simple'|'coaching'|'user'
    customLabels = {},   // { [playerId]: "Label" },
    // PRINT PROPS
    isPrintMode = false,
    isPrinting = false,
    printFilter = 'all', // 'all' | 'offense'
    // WRISTBAND GUIDE
    wristbandGuide = { enabled: false, x: 50, y: 50, w: 40, h: 22.8 },
    onWristbandGuideChange = () => { },
    pickingMotionRouteId = null,
    setPickingMotionRouteId = () => { },
}) => {
    // Merge print mode from props or global settings for "Print Preview" while editing
    const effectivePrintMode = isPrintMode || globalSettings?.printMode;

    const fieldRef = useRef(null);
    const svgRef = useRef(null);

    const [draggingId, setDraggingId] = useState(null);
    const [hoveredPlayerId, setHoveredPlayerId] = useState(null);

    // Route Point Editing
    const [draggingPoint, setDraggingPoint] = useState(null); // { routeId, index }
    const [potentialNextPoint, setPotentialNextPoint] = useState(null); // For straight line preview

    const [draggingZoneId, setDraggingZoneId] = useState(null);

    // Landscape Panning
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef({ x: 0, y: 0 });

    // Multi-Select Marquee
    const [selectionBox, setSelectionBox] = useState(null); // { start: {x,y}, current: {x,y} }

    // Wristband Guide Dragging
    const [isDraggingGuide, setIsDraggingGuide] = useState(false);
    const guideDragStartRef = useRef({ x: 0, y: 0, startX: 50, startY: 50 });

    // Dynamic Aspect Correction
    const [playerScale, setPlayerScale] = useState({ x: 1, y: 1 });

    useEffect(() => {
        const updateScale = () => {
            if (fieldRef.current) {
                const { width, height } = fieldRef.current.getBoundingClientRect();
                if (width > 0 && height > 0) {
                    const ratio = height / width;
                    setPlayerScale({ x: ratio, y: 1 });
                }
            }
        };

        const observer = new ResizeObserver(updateScale);
        if (fieldRef.current) observer.observe(fieldRef.current);

        updateScale();
        return () => observer.disconnect();
    }, []);

    // Auto-Focus on Players (Landscape)
    useEffect(() => {
        if (viewMode === 'landscape' && players.length > 0) {
            // Find bounding box of players
            const ys = players.map(p => p.y);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);
            const centerY = (minY + maxY) / 2;

            // Center Y (53.3 x 100 field)
            // If field is 100 long, we want centerY to be in middle (50).
            // offset = 50 - centerY.
            // But we might want/need to clamp it so field doesn't go off screen too much?
            // For now, free float is fine.
            setPanOffset({ x: 0, y: 50 - centerY });
        } else {
            // Reset in portrait
            setPanOffset({ x: 0, y: 0 });
        }
    }, [viewMode, players.length]); // Re-center on view switch or team size change

    // ABSOLUTE free-draw capture
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawingPlayerId, setDrawingPlayerId] = useState(null);
    const [currentAbs, setCurrentAbs] = useState([]);        // [{x,y}, ...]
    const MIN_STEP_PCT = 0.25; // drop micro segments/jitter
    const MIN_MARKUP_STEP_PCT = 0.5; // for telestrator smoothing

    // Snapshot of lineup when playback starts (keeps overlay fixed while running)
    const [originalPositions, setOriginalPositions] = useState({});

    // WALKTHROUGH MARKUP (Telestrator)
    const [markupLines, setMarkupLines] = useState([]); // [{id, points:[], ts}]
    const [currentMarkup, setCurrentMarkup] = useState(null); // {points:[]}
    const markupStartRef = useRef(null); // { time, x, y, moved }
    const MARKUP_LIFE_MS = 5000; // 5 seconds total (3s stay + 2s fade)

    // Cleanup markup lines
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            setMarkupLines(prev => {
                const filtered = prev.filter(line => (now - line.ts) < MARKUP_LIFE_MS);
                return filtered.length !== prev.length ? filtered : prev;
            });
        }, 200);
        return () => clearInterval(interval);
    }, []);

    // Wizard HOVER state (for showing yardage)
    const [ballHoverPos, setBallHoverPos] = useState(null);

    /** EXACT element-based mouse→percent helper (prevents any offset/scale drift) */
    const getMousePctFromEl = (e, el) => {
        const rect = el.getBoundingClientRect();
        const clientX = (e.touches && e.touches.length) ? e.touches[0].clientX : ((e.changedTouches && e.changedTouches.length) ? e.changedTouches[0].clientX : e.clientX);
        const clientY = (e.touches && e.touches.length) ? e.touches[0].clientY : ((e.changedTouches && e.changedTouches.length) ? e.changedTouches[0].clientY : e.clientY);
        return {
            x: ((clientX - rect.left) / rect.width) * 100,
            y: ((clientY - rect.top) / rect.height) * 100,
        };
    };

    /** Visual Feedback for Ball Wizard */
    const getBallWizardSummary = () => {
        if (!ballSpec.enabled || !ballSpec.segments) return [];
        const summary = [];
        ballSpec.segments.forEach((seg, i) => {
            if (seg.kind === 'snap') {
                const to = players.find(p => p.id === seg.toPlayerId);
                summary.push({ id: `snap-${i}`, type: 'snap', label: `SNAP to ${to?.role || 'QB'}`, icon: '🏈' });
            } else if (seg.kind === 'handoff') {
                const to = players.find(p => p.id === seg.toPlayerId);
                summary.push({ id: `handoff-${i}`, type: 'handoff', label: `HANDOFF to ${to?.role || to?.label || 'Player'}`, icon: '🖐️' });
            } else if (seg.kind === 'throw') {
                const to = players.find(p => p.id === seg.toPlayerId);
                summary.push({ id: `throw-${i}`, type: 'throw', label: `PASS to ${to?.role || to?.label || 'Player'}`, icon: '🎯' });
            }
        });
        return summary;
    };

    /** Helper to get a player's position at a specific time t (seconds), respecting route delay. */
    const getPlayerPosAtTimeLocal = (player, t) => {
        return getPlayerPosAtTime(player, t, routes, originalPositions, playbackState.isPlaying, ballSpec, players);
    };

    /** Find earliest time >= startTime where dist(p1, p2) <= HANDOFF_RADIUS_YDS */
    const computeHandoffTimeLocal = (p1, p2, minTime) => {
        return computeHandoffTime(p1, p2, minTime, routes, playbackState.duration || 10, originalPositions, playbackState.isPlaying, ballSpec, players);
    };

    const handleBallWizardFieldClick = (e) => {
        if (isLocked) return false;
        if (selectedTool !== 'ball') return false;

        // Right-Click Concludes the Wizard
        if (e.button === 2) {
            if (ballWizardStep > 1) {
                setBallWizardStep(4);
                return true;
            }
        }

        const p = getMousePctFromEl(e, e.currentTarget);

        // STEP 1: place ball start position (center)
        if (ballWizardStep === 1) {
            const newBallPos = { x: p.x, y: p.y };

            // ensure one ball player
            let ball = players.find((pl) => pl.type === 'ball');
            if (!ball) {
                const id = Date.now();
                ball = {
                    id,
                    type: 'ball',
                    x: newBallPos.x,
                    y: newBallPos.y,
                    label: '',
                    speed: 0,
                };
                setPlayers([...players, ball]);
            } else {
                setPlayers(
                    players.map((pl) =>
                        pl.id === ball.id ? { ...pl, x: newBallPos.x, y: newBallPos.y } : pl
                    )
                );
            }

            setBallSpec({
                ...ballSpec,
                enabled: true,
                startPos: newBallPos,
            });
            setBallWizardStep(2);
            return true;
        }

        // STEP 3: choose throw target point on a receiver route
        if (ballWizardStep === 3) {
            const clickPos = { x: p.x, y: p.y };

            let best = null; // { route, owner, poly, targetU, targetPos, dist }
            for (const route of routes) {
                if (route.mode !== 'absolute-anchored' || !route.pointsAbs || route.pointsAbs.length < 2)
                    continue;
                const owner = players.find((pl) => pl.id === route.playerId);
                if (!owner || owner.type !== 'offense') continue;

                const poly = anchoredPolylineFor(route, owner);
                if (!poly) continue;

                const u = uAlongPolylineClosest(poly, clickPos);
                const posOnRoute = absPositionAtArclen(poly, u);
                const dist = ydDistAbs(posOnRoute, clickPos);

                if (!best || dist < best.dist) {
                    best = {
                        route,
                        owner,
                        poly,
                        targetU: u,
                        targetPos: posOnRoute,
                        dist,
                    };
                }
            }

            if (!best) return true;

            // receiver timing
            const Lyds = absPathLengthYds(best.poly);
            const forty = best.owner.speed || 4.8;
            const yps = forty > 0 ? 40 / forty : 8;
            const totalT = Lyds / yps;

            // Account for route delay
            const delay = parseFloat(best.owner.routeDelay) || 0;
            // best.targetU is fraction of ROUTE. 
            // Time on route = u * totalT.
            // Absolute time = delay + (u * totalT);
            const catchTime = delay + ((best.targetU || 0) * (totalT || 0));

            // find Current Holder (usually QB)
            const holderId = ballSpec.currentHolderId || ballSpec.snapToPlayerId;
            const holder = players.find((pl) => pl.id === holderId);
            if (!holder) return true;

            const ttype = ballSpec.throwType || 'pass';
            // Use profileConfig to get the correct speed key, fallback to flag_12u
            const profileThrowKey = profileConfig?.throwProfileKey || 'flag_12u';

            // Safety check for key existence, fallback to 'hs' if key invalid in table
            const finalThrowKey = THROW_SPEEDS_YPS[ttype][profileThrowKey] ? profileThrowKey : 'flag_12u';
            const ballYps = THROW_SPEEDS_YPS[ttype][finalThrowKey] || 15;

            // Flight calculation
            const lastSeg = (ballSpec.segments || []).slice(-1)[0];
            const minStart = lastSeg ? (lastSeg.kind === 'postCatchCarry' ? lastSeg.startTime : (lastSeg.endTime || 0)) : 0;
            const tDelay = parseFloat(holder.throwDelay) || 0;
            const absoluteMinRelease = minStart + tDelay;

            // Initial guess for release and catch
            let finalTargetPos = best.targetPos;
            let finalTargetU = best.targetU;

            const holderPosAtMin = getPlayerPosAtTimeLocal(holder, absoluteMinRelease);
            let dist = ydDistAbs(holderPosAtMin, finalTargetPos);
            let flightT = dist / ballYps;

            let releaseTime = catchTime - flightT;
            let finalCatchTime = catchTime;

            if (releaseTime < absoluteMinRelease) {
                // If Throw Delay or previous segments push release too late for the target catch spot,
                // we force the catch to happen later where the WR will be.
                releaseTime = absoluteMinRelease;
                finalCatchTime = releaseTime + flightT;

                // Recalculate WR intercept spot at this new time
                finalTargetU = (totalT > 0) ? clamp01((finalCatchTime - delay) / totalT) : 0;
                finalTargetPos = absPositionAtArclen(best.poly, finalTargetU);

                // One more refinement of distance/time
                dist = ydDistAbs(holderPosAtMin, finalTargetPos);
                flightT = dist / ballYps;
                finalCatchTime = releaseTime + flightT;
                finalTargetU = (totalT > 0) ? clamp01((finalCatchTime - delay) / totalT) : 0;
                finalTargetPos = absPositionAtArclen(best.poly, finalTargetU);
            }

            // NOW fix the positions for the segment so render loop doesn't have to guess
            const finalReleasePos = getPlayerPosAtTimeLocal(holder, releaseTime);

            // Cap the previous Carry segment if it exists
            const segments = [...(ballSpec.segments || [])];
            if (segments.length > 0) {
                const prev = segments[segments.length - 1];
                if (prev.kind === 'postCatchCarry') {
                    // Update ends time of carry to release time of this throw
                    segments[segments.length - 1] = {
                        ...prev,
                        endTime: releaseTime
                    };
                }
            }

            const throwSeg = {
                kind: 'throw',
                fromPlayerId: holder.id,
                toPlayerId: best.owner.id,
                routeId: best.route.id,

                // TIMES
                releaseTime: releaseTime,
                catchTime: finalCatchTime,

                // Compatibility alias for generic loop
                startTime: releaseTime,
                endTime: finalCatchTime,

                // POSITIONS (Snapshotted)
                fromPos: finalReleasePos,
                toPos: finalTargetPos,

                targetU: finalTargetU
            };

            const postCatch = {
                kind: 'postCatchCarry',
                playerId: best.owner.id,
                startTime: finalCatchTime,
                endTime: 100, // Effectively end of play
            };

            setBallSpec({
                ...ballSpec,
                currentHolderId: best.owner.id,
                segments: [...segments, throwSeg, postCatch],

            });

            // Wizard Complete (prevent ghost cursor)
            setBallWizardStep(4);
            return true;
        }

        return false;
    };

    const applyLibraryRoute = (player, routeId, packId) => {
        const library = createLibrary();
        const pack = library.getPack(packId);
        const routeDef = pack?.routes.find(r => r.id === routeId);
        if (!routeDef || !player) return;

        onBeginChange?.();

        const W_YARDS = 53.3;
        const H_YARDS = 100.0;

        // Side-awareness flipping logic
        const tags = routeDef.tags || [];
        const isOffense = player.type === 'offense';
        const isLeft = player.x < 50;

        const processedPoints = routeDef.points.map(pt => {
            let dx = pt.x;
            let dy = pt.y;

            // Refined Flipping Logic:
            // Assuming presets are defined with x > 0 as "Base" direction.
            let flip = false;
            if (tags.includes('in')) {
                // 'In' on right side must flip left
                if (!isLeft) flip = true;
            } else if (tags.includes('out') || tags.includes('pivot')) {
                // 'Out' on left side must flip left
                if (isLeft) flip = true;
            } else if (tags.includes('vert')) {
                // Mirror vertical offsets on right side
                if (!isLeft) flip = true;
            }

            if (flip) dx = -dx;

            const yardX = (player.x * W_YARDS / 100) + dx;
            const yardY = (player.y * H_YARDS / 100) + dy;

            return {
                x: (yardX / W_YARDS) * 100,
                y: (yardY / H_YARDS) * 100
            };
        });

        const newRoute = {
            id: Date.now(),
            playerId: player.id,
            pointsAbs: processedPoints,
            pointsRaw: processedPoints,
            mode: 'absolute-anchored',
            color: isOffense ? (globalSettings.offenseRouteColor || player.color) : (globalSettings.defenderRouteColor || player.color),
            thickness: drawingSettings.lineThickness || 0.3,
            type: drawingSettings.type || 'solid'
        };

        setRoutes(prev => {
            const others = prev.filter(r => r.playerId !== player.id);
            return [...others, newRoute];
        });

        if (tags.includes('zone')) {
            const lastPt = processedPoints[processedPoints.length - 1];
            setPlayers(prev => prev.map(pl => {
                if (pl.id !== player.id) return pl;
                return {
                    ...pl,
                    zoneParams: {
                        enabled: true,
                        relative: false,
                        x: lastPt.x,
                        y: lastPt.y,
                        w: 16,
                        h: 12,
                        alpha: 0.3,
                        color: pl.color || '#3b82f6'
                    }
                };
            }));
        }
    };

    /* ---------------- Drag / Select ---------------- */
    const onMouseDownPlayer = (e, id) => {
        // HANDLER for Walkthrough Markup (Right-Click or Middle-Click)
        if (globalSettings.walkthroughMode && (e.button === 2 || e.button === 1)) {
            // markupStartRef will be set in onMouseDownContainer which fires after if not stopped
            // but we need to stop propagation here if we want to handle it ourselves
            // ACTUALLY: let it bubble to container? No, players have stopPropagation often.
            // Let's handle it here too.
            const pct = getMousePctFromEl(e, fieldRef.current);
            setCurrentMarkup({ points: [pct] });
            markupStartRef.current = { time: Date.now(), x: e.clientX, y: e.clientY, moved: false };
            return;
        }

        // Carry on to selection logic below, but we'll block dragging later in this function

        if (selectedTool === 'ball' && ballWizardStep === 2) {
            e.stopPropagation();

            const qb = players.find((pl) => pl.id === id);
            if (!qb || !ballSpec.startPos) return;

            const startPos = ballSpec.startPos;
            const qbPos = { x: qb.x, y: qb.y };
            const snapDistYds = ydDistAbs(startPos, qbPos);

            let snapDuration = snapDistYds / SNAP_SPEED_YPS;
            snapDuration = Math.max(
                MIN_SNAP_DURATION,
                Math.min(MAX_SNAP_DURATION, snapDuration)
            );

            const snapEndTime = ballSpec.snapTime ?? 0.5;
            const snapStartTime = Math.max(0, snapEndTime - snapDuration);

            const snapSegment = {
                kind: 'snap',
                fromPos: startPos,
                toPlayerId: qb.id,
                startTime: snapStartTime,
                endTime: snapEndTime,
            };

            setBallSpec({
                ...ballSpec,
                enabled: true,
                snapToPlayerId: id,
                currentHolderId: id, // QB starts with ball
                snapTime: snapEndTime,
                segments: [snapSegment],
            });

            setBallWizardStep(3);
            setSelectedPlayerId?.(qb.id);  // Highlight QB
            setSelectedPlayerIds(new Set([qb.id]));
            return;
        }

        // STEP 3: Handoff selection (clicking a player instead of a route)
        if (selectedTool === 'ball' && ballWizardStep === 3) {
            e.stopPropagation();

            const receiver = players.find(p => p.id === id);
            const holderId = ballSpec.currentHolderId;
            const holder = players.find(p => p.id === holderId);

            if (!receiver || !holder) return;
            if (receiver.id === holder.id) {
                // Clicking the holder again finishes the wizard sequence
                setBallWizardStep(4);
                return;
            }

            // Determine previous segment end time
            const lastSeg = (ballSpec.segments || []).slice(-1)[0];
            const minTime = lastSeg ? (lastSeg.kind === 'postCatchCarry' ? lastSeg.startTime : (lastSeg.endTime || 0)) : 0;

            const handoffTime = computeHandoffTimeLocal(holder, receiver, minTime);

            if (handoffTime === null) {
                alert("Players do not get close enough for a handoff!");
                return;
            }

            const handoffSeg = {
                kind: 'handoff',
                fromPlayerId: holder.id,
                toPlayerId: receiver.id,
                startTime: handoffTime,
                endTime: handoffTime + 0.5 // momentary exchange
            };

            const postHandoffCarry = {
                kind: 'postCatchCarry', // Re-use carry logic
                playerId: receiver.id,
                startTime: handoffTime + 0.5,
                endTime: 100
            };

            // Remove previous open-ended carry if exists
            const newSegments = [...(ballSpec.segments || [])];
            if (newSegments.length > 0 && newSegments[newSegments.length - 1].kind === 'postCatchCarry') {
                newSegments.pop();
            }

            setBallSpec({
                ...ballSpec,
                currentHolderId: receiver.id,
                segments: [...newSegments, handoffSeg, postHandoffCarry]
            });

            setSelectedPlayerId?.(receiver.id);  // Highlight handoff receiver
            setSelectedPlayerIds(new Set([receiver.id]));
            // Stay in step 3 to allow subsequent handoffs or throw
            return;
        }

        if (selectedTool === 'draw') {
            if (isLocked && !globalSettings.walkthroughMode) return;

            // Right click in Draw mode returns to Select
            if (e.button === 2) {
                onContextMenu?.(e, 'exit-draw');
                return;
            }

            startDrawFromPlayer(e, id);
            return;
        }

        if (['offense', 'defense', 'ball'].includes(selectedTool)) {
            if (e.button === 2) {
                onContextMenu?.(e, 'exit-draw');
                return;
            }
            // STOP PROPAGATION so clicking a player doesn't trigger onFieldClick (which would add another player on top)
            e.stopPropagation();
            return;
        }
        if (selectedTool === 'select') {
            e.stopPropagation();
            if (e.shiftKey) {
                setSelectedPlayerIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                });
            } else {
                setSelectedPlayerId?.(id);
                setSelectedPlayerIds?.(new Set([id]));
            }

            if (!isLocked || globalSettings.walkthroughMode) {
                // In walkthrough mode, only allow dragging ghost players OR allow all if not locked (handled by outer check)
                if (globalSettings.walkthroughMode) {
                    const p = players.find(pl => pl.id === id);
                    if (p?.isWalkthrough || !isLocked) { // Allow regular players if not locked, OR ghost players always
                        onBeginChange?.();
                        setDraggingId(id);
                    }
                } else {
                    onBeginChange?.();
                    setDraggingId(id);
                }
            }
        }
    };

    const onMouseDownContainer = (e) => {
        // HANDLER for Walkthrough Markup (Right-Click)
        if (globalSettings.walkthroughMode && (e.button === 2 || e.button === 1)) {
            const pct = getMousePctFromEl(e, fieldRef.current);
            setCurrentMarkup({ points: [pct] });
            markupStartRef.current = { time: Date.now(), x: e.clientX, y: e.clientY, moved: false };
            return;
        }

        if (selectedTool === 'select' && e.button === 0) {
            const p = getMousePctFromEl(e, e.currentTarget);
            setSelectionBox({ start: p, current: p });
            if (!e.shiftKey) {
                setSelectedPlayerIds?.(new Set());
                setSelectedPlayerId?.(null);
            }
        }
    }

    const onClickPlayer = (e, id) => {
        e.stopPropagation();
        if (selectedTool === 'select') setSelectedPlayerId?.(id);
    };

    const onMouseMoveContainer = (e) => {
        const p = getMousePctFromEl(e, e.currentTarget);

        // MARKUP DRAWING
        if (currentMarkup) {
            const dist = Math.sqrt(Math.pow(e.clientX - markupStartRef.current.x, 2) + Math.pow(e.clientY - markupStartRef.current.y, 2));
            if (dist > 5) markupStartRef.current.moved = true;

            const pct = getMousePctFromEl(e, fieldRef.current);
            const lastPoint = currentMarkup.points[currentMarkup.points.length - 1];
            // Only add if the new point is sufficiently far from the last one
            if (!lastPoint || distPct(pct, lastPoint) >= MIN_MARKUP_STEP_PCT) {
                setCurrentMarkup(prev => ({
                    ...prev,
                    points: [...prev.points, pct]
                }));
            }
            return; // Prevent other drag/select logic while drawing markup
        }

        if (draggingZoneId && selectedTool === 'select') {
            const pl = players.find(p => p.id === draggingZoneId);
            if (pl) {
                const zp = pl.zoneParams || {};
                const isRelative = !!zp.relative; // Default false

                // Calculate current center
                const currentX = isRelative ? (pl.x + (zp.x || 0)) : (zp.x ?? pl.x);
                const currentY = isRelative ? (pl.y + (zp.y || 0)) : (zp.y ?? pl.y);

                const dx = p.x - currentX;
                const dy = p.y - currentY;

                const baseValueX = isRelative ? (zp.x || 0) : (zp.x ?? pl.x);
                const baseValueY = isRelative ? (zp.y || 0) : (zp.y ?? pl.y);

                setPlayers(players.map(plItem => plItem.id === draggingZoneId ? {
                    ...plItem,
                    zoneParams: {
                        ...plItem.zoneParams,
                        x: baseValueX + dx,
                        y: baseValueY + dy
                    }
                } : plItem));
            }
        }

        if (draggingId && selectedTool === 'select') {
            const dx = p.x - players.find(pl => pl.id === draggingId).x;
            const dy = p.y - players.find(pl => pl.id === draggingId).y;

            // Move all selected players together if dragging one of them
            if (selectedPlayerIds.has(draggingId)) {
                setPlayers(players.map(pl => selectedPlayerIds.has(pl.id) ? { ...pl, x: pl.x + dx, y: pl.y + dy } : pl));
            } else {
                setPlayers(players.map(pl => pl.id === draggingId ? { ...pl, x: p.x, y: p.y } : pl));
            }
        }

        if (selectionBox) {
            setSelectionBox(prev => ({ ...prev, current: p }));
        }

        if (draggingPoint) {
            const { routeId, index } = draggingPoint;
            setRoutes(routes.map(r => {
                if (r.id !== routeId) return r;
                const newPoints = [...r.pointsAbs];

                const owner = players.find(p => p.id === r.playerId);
                if (!owner) return r;

                // Subtract player offset to find the correct absolute point
                const dx = owner.x - (r.drawBase?.x ?? r.pointsAbs[0].x);
                const dy = owner.y - (r.drawBase?.y ?? r.pointsAbs[0].y);

                let targetPos = { x: p.x - dx, y: p.y - dy };

                // Shift Snap while dragging
                if (e.shiftKey) {
                    if (index > 0) {
                        targetPos = snapPoint(newPoints[index - 1], targetPos);
                    } else if (index < newPoints.length - 1) {
                        targetPos = snapPoint(newPoints[index + 1], targetPos);
                    }
                }

                newPoints[index] = targetPos;
                return { ...r, pointsAbs: newPoints, pointsRaw: newPoints };
            }));
        }

        // Wizard Distance Preview
        if (selectedTool === 'ball' && ballWizardStep === 3) {
            setBallHoverPos(p);
        } else if (ballHoverPos) {
            setBallHoverPos(null);
        }
    };

    const onMouseUpContainer = (e) => {
        // MARKUP FINISH
        if (currentMarkup) {
            if (currentMarkup.points.length > 2) { // Require at least 3 points for a meaningful line
                setMarkupLines(prev => [...prev, {
                    id: Date.now() + Math.random(), // Unique ID for key
                    points: currentMarkup.points,
                    ts: Date.now() // Timestamp for potential cleanup
                }]);
            }
            setCurrentMarkup(null);
            return;
        }

        if (isPanning) {
            setIsPanning(false);
            return;
        }

        if (draggingId) setDraggingId(null);
        if (draggingZoneId) setDraggingZoneId(null);
        if (draggingPoint) setDraggingPoint(null);

        if (selectionBox) {
            const x1 = Math.min(selectionBox.start.x, selectionBox.current.x);
            const x2 = Math.max(selectionBox.start.x, selectionBox.current.x);
            const y1 = Math.min(selectionBox.start.y, selectionBox.current.y);
            const y2 = Math.max(selectionBox.start.y, selectionBox.current.y);

            const inBox = players.filter(p => p.x >= x1 && p.x <= x2 && p.y >= y1 && p.y <= y2).map(p => p.id);
            const newSelection = new Set(inBox);
            setSelectedPlayerIds(newSelection);
            if (inBox.length === 1) setSelectedPlayerId?.(inBox[0]);
            else setSelectedPlayerId?.(null);

            setSelectionBox(null);
        }
    };

    /* ---------------- Add tokens on field click ---------------- */
    const onFieldClick = (e) => {
        if (isLocked && !globalSettings.walkthroughMode) return;
        if (e.button !== 0) return; // ONLY left click adds players

        if (selectedTool === 'ball') {
            const handled = handleBallWizardFieldClick(e);
            if (handled) return;
            if (ballWizardStep > 1) return;
        }
        if (selectedTool === 'draw') {
            if (drawingSettings?.startPos) {
                onBeginChange?.();
                setIsDrawing(true);
                setDrawingPlayerId(selectedPlayerId);
                // Start with the initial anchor plus the first point at mouse click
                const p = getMousePctFromEl(e, e.currentTarget);
                setCurrentAbs([drawingSettings.startPos, p]);
            }
            return;
        }
        onBeginChange?.();
        const p = getMousePctFromEl(e, e.currentTarget);
        const id = Date.now() + Math.random();
        const isWalkthrough = !!globalSettings?.walkthroughMode;

        if (selectedTool === 'offense') {
            const newPlayer = {
                id,
                type: 'offense',
                x: p.x,
                y: p.y,
                label: 'P',
                role: 'WR',
                speed: 5.5,
                color: globalSettings?.offenseColor || '#fbbf24',
                routeColor: globalSettings?.offenseRouteColor || '#fbbf24',
                isWalkthrough
            };
            setPlayers(prev => [...prev, newPlayer]);
            setSelectedPlayerId?.(id);
            setSelectedPlayerIds?.(new Set([id]));
        } else if (selectedTool === 'defense') {
            const newPlayer = {
                id,
                type: 'defense',
                x: p.x,
                y: p.y,
                label: 'X',
                role: 'DEF',
                speed: 5.5,
                color: globalSettings?.defenderColor || '#ef4444',
                routeColor: globalSettings?.defenderRouteColor || '#ef4444',
                isWalkthrough
            };
            setPlayers(prev => [...prev, newPlayer]);
            setSelectedPlayerId?.(id);
            setSelectedPlayerIds?.(new Set([id]));
        } else if (selectedTool === 'ball') {
            const newPlayer = { id, type: 'ball', x: p.x, y: p.y, label: '', speed: 0 };
            setPlayers(prev => [...prev, newPlayer]);
            setSelectedPlayerId?.(id);
            setSelectedPlayerIds?.(new Set([id]));
        }
    };

    /* ---------------- ABSOLUTE Free-draw (anchored) ----------------
       Store absolute points + drawBase (player pos at draw time).
    --------------------------------------------------------------- */
    const startDrawFromPlayer = (e, playerId) => {
        onBeginChange?.();
        e.stopPropagation();
        const player = players.find(p => p.id === playerId);
        if (!player) return;
        setIsDrawing(true);
        setDrawingPlayerId(playerId);
        setSelectedPlayerId?.(playerId);

        // Start exactly at the player’s current position
        setCurrentAbs([{ x: player.x, y: player.y }]);
    };

    const onDrawMove = (e) => {
        if (!isDrawing || selectedTool !== 'draw') return;
        const m = getMousePctFromEl(e, e.currentTarget);
        const clamped = { x: Math.max(0, Math.min(100, m.x)), y: Math.max(0, Math.min(100, m.y)) };

        // PRECISION ASSIST: Stem Snapping
        // Snap the first segment to a vertical line if close enough
        let finalPoint = clamped;
        if (currentAbs.length === 1) {
            const start = currentAbs[0];
            const dx = Math.abs(clamped.x - start.x);
            if (dx < 3) {
                finalPoint = { ...clamped, x: start.x };
            }
        }

        if (straightLineMode) {
            // Shift snap while moving preview
            if (e.shiftKey && currentAbs.length > 0) {
                const last = currentAbs[currentAbs.length - 1];
                setPotentialNextPoint(snapPoint(last, finalPoint));
            } else {
                setPotentialNextPoint(finalPoint);
            }
            return;
        }

        setCurrentAbs(prev => {
            const last = prev[prev.length - 1];
            if (!last || distPct(finalPoint, last) >= MIN_STEP_PCT) return [...prev, finalPoint];
            return prev;
        });
    };

    const handleOverlayClick = (e) => {
        if (!isDrawing || !straightLineMode) return;

        // Use potentialNextPoint if available (it has stem/angle snapping applied)
        const ptToAdd = potentialNextPoint || getMousePctFromEl(e, e.currentTarget);

        setCurrentAbs(prev => [...prev, ptToAdd]);
    };

    const onDrawEnd = () => {
        if (!isDrawing) return;
        setIsDrawing(false);

        // Dedup tiny steps; keep ≥2 points
        const cleaned = [];
        // If straightLineMode, we add the potential next point as the destination
        const raw = straightLineMode && potentialNextPoint ? [...currentAbs, potentialNextPoint] : currentAbs;

        for (const pt of raw) {
            if (!cleaned.length || distPct(pt, cleaned[cleaned.length - 1]) >= MIN_STEP_PCT) cleaned.push(pt);
        }

        if (cleaned.length >= 2) {
            const player = players.find(p => p.id === drawingPlayerId);
            const drawBase = player ? { x: player.x, y: player.y } : cleaned[0];

            const isOption = drawingSettings?.type === 'option';
            const others = routes.filter(r => {
                if (r.playerId !== drawingPlayerId) return true;
                if (isOption) return r.type !== 'option'; // Replace existing option
                return r.type === 'option'; // Keep existing option if drawing primary
            });

            let newRoute = {
                id: Date.now(),
                playerId: drawingPlayerId,
                mode: 'absolute-anchored',
                pointsAbs: cleaned,
                pointsRaw: cleaned,
                drawBase,
                color: player?.routeColor || drawingSettings?.color || 'yellow',
                type: drawingSettings?.type || 'solid',
                isStraight: straightLineMode,
                isFreeHand: true,
            };

            // Auto-Process (Smooth/Straighten) if enabled (SKIP if straight line mode)
            if (autoProcessOptions && !straightLineMode) {
                newRoute = processRoute(newRoute, autoProcessOptions.smooth, autoProcessOptions.straighten);
            }

            setRoutes([
                ...others,
                newRoute
            ]);
        }
        setCurrentAbs([]);
        setPotentialNextPoint(null);
        setDrawingPlayerId(null);
        // Reset drawing settings (clear option mode and startPos)
        setDrawingSettings(prev => ({ ...prev, type: 'solid', startPos: null }));
    };

    // Helper for snapping points to 45 degree increments
    const snapPoint = (pivot, target) => {
        const dx = target.x - pivot.x;
        const dy = target.y - pivot.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let angle = Math.atan2(dy, dx);
        const step = Math.PI / 4; // 45 degrees
        angle = Math.round(angle / step) * step;
        return {
            x: pivot.x + dist * Math.cos(angle),
            y: pivot.y + dist * Math.sin(angle)
        };
    };

    const onRightClick = (e) => {
        e.preventDefault();
        if (['offense', 'defense', 'ball', 'draw'].includes(selectedTool)) {
            if (isDrawing && straightLineMode) {
                onDrawEnd();
            }
            onContextMenu?.(e, 'exit-draw');
        } else {
            const p = getMousePctFromEl(e, fieldRef.current);
            onContextMenu?.(e, 'background', null, p);
        }
    };

    /* ---------------- Playback snapshot ---------------- */
    useEffect(() => {
        if (playbackState.isPlaying) {
            setOriginalPositions(prev => {
                if (Object.keys(prev).length) return prev;
                const m = {};
                players.forEach(p => { m[p.id] = { x: p.x, y: p.y }; });
                return m;
            });
        } else if (playbackState.progress === 0 && Object.keys(originalPositions).length) {
            setOriginalPositions({});
        }
    }, [playbackState.isPlaying, playbackState.progress, players, originalPositions]);

    /* ---------------- Build anchored polylines for overlay & animation ---------------- */
    const anchoredPolylineFor = (route, owner) => {
        if (!route || !owner || !route.pointsAbs || route.pointsAbs.length < 2) return null;

        // Editing: anchor to CURRENT player position so line follows while dragging
        // Playing: anchor to ORIGINAL lineup so overlay stays fixed while token runs
        const anchorPos = playbackState.isPlaying
            ? (originalPositions[owner.id] || { x: owner.x, y: owner.y })
            : { x: owner.x, y: owner.y };

        const dx = anchorPos.x - (route.drawBase?.x ?? route.pointsAbs[0].x);
        const dy = anchorPos.y - (route.drawBase?.y ?? route.pointsAbs[0].y);
        return shiftPolyline(route.pointsAbs, dx, dy);
    };

    /* ---------------- Display positions (run along anchored ABS routes) ---------------- */
    /* ---------------- Dynamic Ball Timeline (Recalculated on the fly) ---------------- */
    const dynamicSegments = useMemo(() => {
        return getDynamicBallSegments(ballSpec, players, routes, profileConfig, originalPositions, playbackState.isPlaying);
    }, [ballSpec, players, routes, profileConfig, originalPositions, playbackState.isPlaying]);

    /* ---------------- Display positions (run along anchored ABS routes) ---------------- */
    const displayPosById = useMemo(() => {
        const map = {};
        const duration = playbackState.duration || 10;
        const elapsedSec = playbackState.progress * duration;

        // 1. Calculate Standard Player Positions First
        for (const p of players) {
            if (p.type === 'ball') continue;
            map[p.id] = getPlayerPosAtTimeLocal(p, elapsedSec);
        }

        // 2. Calculate Ball Position based on dynamic segments
        const ballPlayer = players.find((pl) => pl.type === 'ball');
        if (ballPlayer && ballSpec && ballSpec.enabled && ballSpec.startPos) {
            const t = elapsedSec;
            let pos = ballSpec.startPos;
            let activeSeg = null;

            for (const seg of dynamicSegments) {
                if (t >= seg.startTime && t <= (seg.endTime || 100)) {
                    activeSeg = seg;
                    break;
                }
            }

            if (activeSeg) {
                if (activeSeg.kind === 'snap') {
                    const qbPos = map[activeSeg.toPlayerId] || ballSpec.startPos;
                    const u = clamp01((t - activeSeg.startTime) / (activeSeg.endTime - activeSeg.startTime));
                    pos = {
                        x: activeSeg.fromPos.x + (qbPos.x - activeSeg.fromPos.x) * u,
                        y: activeSeg.fromPos.y + (qbPos.y - activeSeg.fromPos.y) * u
                    };
                }
                else if (activeSeg.kind === 'handoff') {
                    // Interpolate between carrier and receiver
                    const p1 = map[activeSeg.fromPlayerId];
                    const p2 = map[activeSeg.toPlayerId];
                    if (p1 && p2) {
                        const u = clamp01((t - activeSeg.startTime) / (activeSeg.endTime - activeSeg.startTime));
                        pos = {
                            x: p1.x + (p2.x - p1.x) * u,
                            y: p1.y + (p2.y - p1.y) * u
                        };
                    }
                }
                else if (activeSeg.kind === 'throw') {
                    // Pre-calculated in wizard step 3
                    const fromPos = activeSeg.fromPos || ballSpec.startPos;
                    const toPos = activeSeg.toPos || ballSpec.startPos;

                    const u = clamp01((t - activeSeg.releaseTime) / (activeSeg.catchTime - activeSeg.releaseTime));

                    // Arc effect: Height simulation via scale
                    // Scale arc by throw distance - short passes have less arc
                    const throwDistYds = ydDistAbs(fromPos, toPos);
                    const arcFactor = Math.min(1.0, throwDistYds / 30); // Max arc at 30+ yards
                    const arcHeight = arcFactor * 0.6 * Math.sin(u * Math.PI);
                    const scale = 1 + arcHeight;

                    // Rotate to face target - add -90° offset since ball SVG is vertical
                    const dx = toPos.x - fromPos.x;
                    const dy = toPos.y - fromPos.y;
                    const angleRad = Math.atan2(dy, dx);
                    const angleDeg = (angleRad * 180 / Math.PI) - 90; // -90° offset for vertical ball

                    pos = {
                        x: fromPos.x + dx * u,
                        y: fromPos.y + dy * u,
                        scale,
                        rotation: angleDeg,
                        isThrowing: true  // Ball is in flight - enable spin
                    };
                }
                else if (activeSeg.kind === 'postCatchCarry') {
                    // Ball follows receiver - get their position and route direction
                    const carrierPos = map[activeSeg.playerId] || ballSpec.startPos;
                    // Calculate carrier's heading from their route for ball orientation
                    const carrier = players.find(p => p.id === activeSeg.playerId);
                    let carrierRotation = -90; // Default vertical (facing upfield)
                    if (carrier) {
                        const route = routes.find(r => r.playerId === carrier.id && r.mode === 'absolute-anchored');
                        if (route && route.pointsAbs && route.pointsAbs.length >= 2) {
                            // Find current direction along route based on time
                            const pts = route.pointsAbs;
                            const last = pts[pts.length - 1];
                            const prev = pts[pts.length - 2];
                            // Add -90° offset since ball SVG is vertical
                            carrierRotation = Math.atan2(last.y - prev.y, last.x - prev.x) * 180 / Math.PI - 90;
                        }
                    }
                    pos = { ...carrierPos, rotation: carrierRotation, isThrowing: false };
                }
            } else {
                // No active segment? 
                const firstSeg = dynamicSegments[0];
                if (firstSeg && t < firstSeg.startTime) {
                    pos = ballSpec.startPos;
                } else {
                    // Find most recent finished segment to determine holder
                    let lastFnseg = null;
                    for (const seg of dynamicSegments) {
                        if ((seg.endTime || seg.catchTime) < t) lastFnseg = seg;
                    }

                    if (lastFnseg) {
                        // Ball is held by 'toPlayerId' or 'playerId' of last segment
                        const holderId = lastFnseg.toPlayerId || lastFnseg.playerId || lastFnseg.playerId;
                        if (holderId) {
                            pos = map[holderId] || ballSpec.startPos;
                        }
                    }
                }
            }

            map[ballPlayer.id] = pos;
        }

        return map;
    }, [players, routes, playbackState.progress, playbackState.isPlaying, originalPositions, ballSpec]);

    const isNearAnyRoute = (pos) => {
        if (!pos) return false;
        for (const route of routes) {
            if (route.mode !== 'absolute-anchored' || !route.pointsAbs || route.pointsAbs.length < 2) continue;
            const owner = players.find(p => p.id === route.playerId);
            if (!owner || owner.type !== 'offense') continue;
            const poly = anchoredPolylineFor(route, owner);
            if (!poly) continue;
            const u = uAlongPolylineClosest(poly, pos);
            const posOnRoute = absPositionAtArclen(poly, u);
            if (ydDistAbs(posOnRoute, pos) < 3.5) return true;
        }
        return false;
    };

    // Cursor Logic
    const getCursor = () => {
        if (isPanning) return 'grabbing';
        if (isLocked) return 'not-allowed';
        if (playbackState.isPlaying) return 'default';
        if (selectedTool === 'select') {
            if (draggingId) return 'grabbing';
            if (hoveredPlayerId) return 'grab';
            return 'default';
        }
        if (selectedTool === 'ball') {
            const handUrl = `url('data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 11V6a2 2 0 0 0-4 0v5m-4 0V4a2 2 0 0 0-4 0v9m4-2V5a2 2 0 1 0-4 0v10m12-2a2 2 0 0 0-4 0v2m-6 4a6 6 0 0 1-6-6V9" stroke="%23fbbf24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>') 12 12, auto`;
            const redXUrl = `url('data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="%23ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>') 12 12, auto`;
            const redReticleUrl = `url('data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" stroke="%23ef4444" stroke-width="2" stroke-dasharray="8,4" fill="none"/><line x1="16" y1="2" x2="16" y2="8" stroke="%23ef4444" stroke-width="2"/><line x1="16" y1="24" x2="16" y2="30" stroke="%23ef4444" stroke-width="2"/><line x1="2" y1="16" x2="8" y2="16" stroke="%23ef4444" stroke-width="2"/><line x1="24" y1="16" x2="30" y2="16" stroke="%23ef4444" stroke-width="2"/><rect x="15" y="15" width="2" height="2" fill="%23ef4444"/><path d="M8 8 L10 10 M22 8 L24 10 M8 22 L10 24 M22 22 L24 24" stroke="%23ef4444" stroke-width="2"/></svg>') 16 16, auto`;
            const greenReticleUrl = `url('data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" stroke="%2310b981" stroke-width="2" stroke-dasharray="8,4" fill="none"/><line x1="16" y1="2" x2="16" y2="8" stroke="%2310b981" stroke-width="2"/><line x1="16" y1="24" x2="16" y2="30" stroke="%2310b981" stroke-width="2"/><line x1="2" y1="16" x2="8" y2="16" stroke="%2310b981" stroke-width="2"/><line x1="24" y1="16" x2="30" y2="16" stroke="%2310b981" stroke-width="2"/><rect x="15" y="15" width="2" height="2" fill="%2310b981"/><path d="M8 8 L10 10 M22 8 L24 10 M8 22 L10 24 M22 22 L24 24" stroke="%2310b981" stroke-width="2"/></svg>') 16 16, auto`;

            if (ballWizardStep === 1) return `url('data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H8l4-5 4 5h-3v4h-2z" fill="%23fbbf24"/></svg>') 16 16, auto`;
            if (ballWizardStep === 2) {
                if (hoveredPlayerId && players.find(p => p.id === hoveredPlayerId)?.type === 'offense') {
                    return handUrl;
                }
                return redXUrl;
            }
            if (ballWizardStep === 3) {
                if (hoveredPlayerId && players.find(p => p.id === hoveredPlayerId)?.type === 'offense') {
                    return handUrl;
                }
                return isNearAnyRoute(ballHoverPos) ? greenReticleUrl : redReticleUrl;
            }
        }
        if (selectedTool === 'draw') return 'crosshair';
        return 'default';
    };

    // Pan Handlers
    const handlePanStart = (e) => {
        if (viewMode !== 'landscape') return;
        // Middle click or just background click if not selecting tool?
        // Let's allow background dragging if not drawing/using tool
        if (draggingId || drawingPlayerId || selectedTool === 'draw' || drawingSettings?.startPos || ballWizardStep > 0) return;

        setIsPanning(true);
        const clientY = (e.touches ? e.touches[0].clientY : e.clientY);
        panStartRef.current = { y: clientY, staticY: panOffset.y };
        e.preventDefault(); // Stop scroll
    };

    const handlePanMove = (e) => {
        if (!isPanning) return;
        const clientY = (e.touches ? e.touches[0].clientY : e.clientY);
        const deltaPx = clientY - panStartRef.current.y;

        if (fieldRef.current) {
            const h = fieldRef.current.clientHeight;
            const deltaPct = (deltaPx / h) * 100; // rough % movement
            setPanOffset({ ...panOffset, y: panStartRef.current.staticY + deltaPct });
        }
    };

    const handlePanEnd = () => {
        setIsPanning(false);
    };

    // Scaling Logic for Landscape
    // In landscape (zoomed in), players should be smaller to look proportional.
    let effectiveIconSize = viewMode === 'landscape' ? iconSize * 0.6 : iconSize;
    if (isPrintMode) {
        // Boost size slightly for print visibility
        effectiveIconSize *= 1.25;
    }

    // Wristband Guide Handlers
    const handleGuideDragStart = (e) => {
        if (!wristbandGuide.enabled || isPrintMode) return;
        e.stopPropagation();
        setIsDraggingGuide(true);
        const p = getMousePctFromEl(e, fieldRef.current);
        guideDragStartRef.current = { x: p.x, y: p.y, startX: wristbandGuide.x, startY: wristbandGuide.y };
    };

    const handleGuideDragMove = (e) => {
        if (!isDraggingGuide) return;
        const p = getMousePctFromEl(e, fieldRef.current);
        const dx = p.x - guideDragStartRef.current.x;
        const dy = p.y - guideDragStartRef.current.y;
        onWristbandGuideChange({
            ...wristbandGuide,
            x: clamp01((guideDragStartRef.current.startX + dx) / 100) * 100,
            y: clamp01((guideDragStartRef.current.startY + dy) / 100) * 100,
        });
    };

    // Safety Guards
    if (!Array.isArray(players) || !Array.isArray(routes)) {
        console.warn("Field: Missing players or routes", { players, routes });
        return null;
    }

    return (
        <div
            className={`football-field ${viewMode === 'landscape' ? 'landscape' : ''} ${(isPrintMode || isPrinting) ? 'print-mode' : ''}`}
            ref={fieldRef}
            // Add Panning Listeners
            onMouseDown={(e) => {
                if (isPrintMode) return;
                if (e.target === fieldRef.current || e.target.tagName === 'svg' || e.currentTarget === fieldRef.current) {
                    handlePanStart(e);
                }
                onMouseDownContainer(e);
                onFieldClick(e);
            }}
            onMouseMove={(e) => {
                if (isPanning) {
                    handlePanMove(e);
                } else if (isDraggingGuide) {
                    handleGuideDragMove(e);
                } else {
                    onMouseMoveContainer(e);
                }
            }}
            onMouseUp={(e) => {
                handlePanEnd();
                setIsDraggingGuide(false);
                onMouseUpContainer(e);
            }}
            onMouseLeave={(e) => {
                handlePanEnd();
                setIsDraggingGuide(false);
                onMouseUpContainer(e);
            }}
            onTouchStart={(e) => {
                if (e.target === fieldRef.current || e.target.tagName === 'svg') {
                    handlePanStart(e);
                }
            }}
            onTouchMove={(e) => {
                if (isPanning) {
                    handlePanMove(e);
                } else {
                    if (draggingId) e.preventDefault();
                    onMouseMoveContainer(e);
                }
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(e) => {
                e.preventDefault();
                try {
                    const data = JSON.parse(e.dataTransfer.getData('application/json'));
                    if (data.type === 'route-preset') {
                        const p = getMousePctFromEl(e, fieldRef.current);
                        // Find nearest player to drop point
                        const radius = 8; // pct
                        let targetPlayer = null;
                        let minDist = radius;

                        players.forEach(pl => {
                            const d = distPct({ x: pl.x, y: pl.y }, p);
                            if (d < minDist) {
                                minDist = d;
                                targetPlayer = pl;
                            }
                        });

                        if (targetPlayer) {
                            applyLibraryRoute(targetPlayer, data.routeId, data.packId);
                        } else if (selectedPlayerId) {
                            // Fallback to selected player if dropped on empty space
                            const sel = players.find(pl => pl.id === selectedPlayerId);
                            if (sel) applyLibraryRoute(sel, data.routeId, data.packId);
                        }
                    } else if (data.type === 'roster-player') {
                        const p = getMousePctFromEl(e, fieldRef.current);
                        const radius = 8;
                        let targetPlayer = null;
                        let minDist = radius;

                        players.forEach(pl => {
                            const d = distPct({ x: pl.x, y: pl.y }, p);
                            if (d < minDist) {
                                minDist = d;
                                targetPlayer = pl;
                            }
                        });

                        if (targetPlayer) {
                            onBeginChange?.();
                            setPlayers(prev => prev.map(pl => {
                                if (pl.id !== targetPlayer.id) return pl;
                                return {
                                    ...pl,
                                    name: data.playerData.name,
                                    number: data.playerData.number,
                                    color: data.playerData.color
                                };
                            }));
                        }
                    }
                } catch (err) {
                    console.error("Drop failed", err);
                }
            }}
            onTouchEnd={(e) => {
                handlePanEnd();
                onMouseUpContainer(e);
            }}
            onContextMenu={(e) => {
                if (isPrintMode) return e.preventDefault();

                // SMART RIGHT CLICK for Walkthrough Mode
                if (globalSettings.walkthroughMode) {
                    const now = Date.now();
                    const start = markupStartRef.current;
                    if (start) {
                        const elapsed = now - start.time;
                        const dist = Math.sqrt(Math.pow(e.clientX - start.x, 2) + Math.pow(e.clientY - start.y, 2));
                        // If user dragged more than 10px or held more than 300ms, assume markup/telestrator and BLOCK menu
                        if (dist > 10 || elapsed > 300 || start.moved) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                    }
                }

                e.preventDefault();
                e.stopPropagation();

                // Selection on right click (even background clears or prepares)
                const p = getMousePctFromEl(e, fieldRef.current);
                if (['offense', 'defense', 'ball', 'draw'].includes(selectedTool)) {
                    onContextMenu?.(e, 'exit-draw');
                    return;
                }
                onContextMenu?.(e, 'background', null, p);
            }}
            style={{
                cursor: (isPrintMode || isPrinting) ? 'default' : (isDraggingGuide ? 'move' : getCursor()),
                transform: `translateY(${panOffset.y}%)`,
                background: (isPrintMode || isPrinting) ? '#ffffff' : undefined,
                backgroundImage: (!showGridlines) ? 'none' : undefined
            }}
        >
            {/* draw-capture overlay (Hidden in print) */}
            {!isPrintMode && isDrawing && (
                <div
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 100, cursor: 'crosshair', touchAction: 'none' }}
                    onMouseMove={onDrawMove}
                    onTouchMove={(e) => { e.preventDefault(); onDrawMove(e); }}
                    onMouseUp={straightLineMode ? undefined : onDrawEnd}
                    onClick={handleOverlayClick}
                    onDoubleClick={onDrawEnd}
                    onTouchEnd={straightLineMode ? undefined : onDrawEnd}
                    onMouseLeave={onDrawEnd}
                    onContextMenu={onRightClick}
                />
            )}

            {/* Yard Lines (SVG) - visual guide */}
            {/* (Moved into SVG to ensure alignment with 'meet' aspect ratio) */}

            {/* ROUTES layer (same box as mouse math) */}
            <svg
                ref={svgRef}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 15 }}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
            >
                <defs>
                    {[
                        '#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff',
                        'yellow', 'red', 'blue', 'white', 'black', 'orange', 'green', 'pink', 'violet', 'cyan', 'lime', 'indigo'
                    ].map(c => {
                        const safe = c.replace('#', '');
                        const scale = (drawingSettings?.arrowScale ?? 1);
                        const size = 2 * scale;
                        const refX = size / 2;
                        const tW = 0.35 * scale, tH = 3 * scale; // Thinner T-bar (Block)

                        let printColor = c;

                        return (
                            <React.Fragment key={c}>
                                <marker id={`arrow-${safe}`} markerWidth={size} markerHeight={size}
                                    refX={refX} refY={refX} orient="auto" markerUnits="userSpaceOnUse">
                                    <path d={`M0,0 L0,${size} L${size},${size / 2} z`} fill={printColor} />
                                </marker>
                                <marker id={`tbar-${safe}`} markerWidth={tH} markerHeight={tH}
                                    refX={tW / 2} refY={tH / 2} orient="auto" markerUnits="userSpaceOnUse">
                                    <rect x="0" y="0" width={tW} height={tH} fill={printColor} />
                                </marker>
                            </React.Fragment>
                        );
                    })}
                </defs>

                {(showGridlines !== false) && Array.from({ length: 99 }, (_, i) => i + 1).map(y => {
                    const lineStroke = effectivePrintMode ? "#d1d5db" : "var(--field-line)";
                    const numFill = effectivePrintMode ? "#9ca3af" : "var(--field-number)";
                    const isTen = y % 10 === 0;
                    const isFive = y % 5 === 0;

                    // NFL Dimensions
                    // Width is 53.33 yards. X is 0-100%.
                    // 100 units = 53.33 yards.
                    // Center hashes are approx 18.5 ft wide (6.17 yds).
                    // Left Hash: ~44% X. Right Hash: ~56% X.
                    const leftHashX = 44.2;
                    const rightHashX = 55.8;

                    // Inset Numbers (approx 12 yards from sideline = ~22.5%)
                    const leftNumX = 20;
                    const rightNumX = 80;

                    return (
                        <g key={y}>
                            {/* Full Line every 5 yards */}
                            {isFive && (
                                <line
                                    x1="0" y1={y} x2="100" y2={y}
                                    stroke={lineStroke}
                                    strokeWidth={isTen ? "0.3" : "0.15"}
                                    vectorEffect="non-scaling-stroke"
                                />
                            )}

                            {/* Sideline Ticks (Every Yard) */}
                            {!isFive && (
                                <>
                                    <line x1="0" y1={y} x2="2" y2={y} stroke={lineStroke} strokeWidth="0.15" opacity="0.5" />
                                    <line x1="98" y1={y} x2="100" y2={y} stroke={lineStroke} strokeWidth="0.15" opacity="0.5" />
                                </>
                            )}

                            {/* Hash Marks (Every Yard) */}
                            <line
                                x1={leftHashX - 1} y1={y} x2={leftHashX + 1} y2={y}
                                stroke={lineStroke}
                                strokeWidth="0.15"
                            />
                            <line
                                x1={rightHashX - 1} y1={y} x2={rightHashX + 1} y2={y}
                                stroke={lineStroke}
                                strokeWidth="0.15"
                            />

                            {/* Numbers (Every 10 yards) */}
                            {isTen && !globalSettings.hideYardNumbers && (
                                <g>
                                    <text x={leftNumX} y={y} fill={numFill} fillOpacity="0.5" fontSize="3.5" fontWeight="bold" dominantBaseline="middle" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                                        {y <= 50 ? y : 100 - y}
                                    </text>
                                    <text x={rightNumX} y={y} fill={numFill} fillOpacity="0.5" fontSize="3.5" fontWeight="bold" dominantBaseline="middle" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                                        {y <= 50 ? y : 100 - y}
                                    </text>
                                </g>
                            )}
                        </g>
                    );
                })}

                {/* Center Field Logo (50 Yard Line) */}
                {!effectivePrintMode && showGridlines && (
                    <image
                        href="/logo.png"
                        x="37.5"
                        y="37.5"
                        width="25"
                        height="25"
                        opacity="0.25"
                        style={{ pointerEvents: 'none' }}
                        preserveAspectRatio="xMidYMid meet"
                    />
                )}

                {/* Zone Defense Bubbles */}
                {(!isPrintMode || printFilter === 'all') && players.map(p => {
                    if (!p.zoneParams?.enabled) return null;
                    const zp = p.zoneParams;

                    // Use displayPosById if it follows the player (isRelative)
                    const isRelative = !!zp.relative; // Default false
                    const pos = isRelative ? (displayPosById[p.id] || p) : p;

                    const centerX = isRelative ? (pos.x + (zp.x || 0)) : (zp.x ?? p.x);
                    const centerY = isRelative ? (pos.y + (zp.y || 0)) : (zp.y ?? p.y);

                    const rx = (zp.w || 8) / 2 * (100 / 53.3);
                    const ry = (zp.h || 6) / 2;

                    return (
                        <ellipse
                            key={`zone-${p.id}`}
                            cx={centerX}
                            cy={centerY}
                            rx={rx}
                            ry={ry}
                            fill={zp.color || (p.type === 'offense' ? 'yellow' : 'blue')}
                            fillOpacity={zp.alpha || 0.3}
                            stroke={selectedPlayerId === p.id ? "white" : "none"}
                            strokeWidth="0.2"
                            style={{
                                cursor: selectedTool === 'select' ? 'move' : 'default',
                                pointerEvents: selectedTool === 'select' ? 'all' : 'none'
                            }}
                            onMouseDown={(e) => {
                                if (selectedTool !== 'select' || (isLocked && !globalSettings.walkthroughMode)) return;
                                e.stopPropagation();
                                onBeginChange?.();
                                setDraggingZoneId(p.id);
                                setSelectedPlayerId?.(p.id);
                            }}
                        />
                    );
                })}

                {/* saved routes (ABSOLUTE + ANCHORED) */}
                {routes.map(route => {
                    // Global visibility toggle
                    if (!showRoutes) return null;

                    if (route.mode !== 'absolute-anchored' || !route.pointsAbs || route.pointsAbs.length < 2) return null;
                    const owner = players.find(p => p.id === route.playerId);
                    if (!owner) return null;

                    // Filter for offense only in print mode if needed
                    if (effectivePrintMode && printFilter === 'offense' && owner.type !== 'offense') return null;

                    // Walkthrough Mode Logic: Hide routes unless selected (EXCEPT for ghost players)
                    if (!isPrintMode && globalSettings.walkthroughMode && !selectedPlayerIds.has(owner.id) && !owner.isWalkthrough) {
                        return null;
                    }

                    const poly = anchoredPolylineFor(route, owner);
                    if (!poly) return null;

                    let color = owner.routeColor || route.color || (owner.type === 'offense' ? globalSettings?.offenseRouteColor : globalSettings?.defenderRouteColor) || '#fbbf24';

                    const safe = color.replace('#', '');
                    const isOption = route.type === 'option';
                    const marker = route.type === 'block' ? `url(#tbar-${safe})` : `url(#arrow-${safe})`;

                    // Style Logic: Option overrides owner style
                    let dashArray = owner.routeStyle === 'dashed' ? '5,5' : (owner.routeStyle === 'dotted' ? '1,2' : 'none');
                    let opacity = owner.routeStyle === 'invisible' ? 0 : 1;

                    if (isOption) {
                        dashArray = "0.1, 2.5";
                    }

                    // Motion Logic (Manual Point-Based)
                    let preSnapPoly = null;
                    let postSnapPoly = poly;

                    if (route.motionU !== undefined && route.motionU !== null && route.type !== 'block') {
                        const split = splitPolylineAtU(poly, route.motionU);
                        preSnapPoly = split.pre;
                        postSnapPoly = (route.motionU >= 0.99) ? null : split.post;
                    }

                    return (
                        <g key={route.id}>
                            {/* Hitbox for easier selection / Alt+Click / Motion Picking */}
                            {!isPrintMode && (
                                <path
                                    d={pointsToPath(poly)}
                                    stroke="transparent"
                                    strokeWidth="2.5"
                                    fill="none"
                                    style={{
                                        cursor: pickingMotionRouteId ? 'crosshair' : 'pointer',
                                        pointerEvents: (selectedTool === 'ball') ? 'none' : 'stroke'
                                    }}
                                    onClick={(e) => {
                                        if (pickingMotionRouteId === route.id) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const m = getMousePctFromEl(e, fieldRef.current);

                                            // Calculate u based on click position relative to the ACTUAL path visually shown
                                            const u = findUOnPolyline(poly, m);

                                            onBeginChange?.();
                                            setRoutes(routes.map(r => r.id === route.id ? { ...r, motionU: u } : r));
                                            setPickingMotionRouteId(null);
                                            return;
                                        }

                                        if (e.altKey) {
                                            // Alt+Click to Insert Breakpoint
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const m = getMousePctFromEl(e, fieldRef.current);
                                            // shift m back to absolute coordinates if needed
                                            const anchorPos = { x: owner.x, y: owner.y };
                                            const dx = anchorPos.x - (route.drawBase?.x ?? route.pointsAbs[0].x);
                                            const dy = anchorPos.y - (route.drawBase?.y ?? route.pointsAbs[0].y);
                                            const clickedAbs = { x: m.x - dx, y: m.y - dy };

                                            // Find nearest segment to insert at
                                            let bestIdx = 1;
                                            let minDist = 999;
                                            for (let i = 0; i < route.pointsAbs.length - 1; i++) {
                                                const d = distToSegment(clickedAbs, route.pointsAbs[i], route.pointsAbs[i + 1]);
                                                if (d < minDist) {
                                                    minDist = d;
                                                    bestIdx = i + 1;
                                                }
                                            }

                                            const newPoints = [...route.pointsAbs];
                                            newPoints.splice(bestIdx, 0, clickedAbs);
                                            onBeginChange?.();
                                            setRoutes(routes.map(r => r.id === route.id ? { ...r, pointsAbs: newPoints, pointsRaw: newPoints } : r));
                                        }
                                    }}
                                    onContextMenu={(e) => {
                                        if (effectivePrintMode) return;
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedPlayerId?.(route.playerId);
                                        onContextMenu?.(e, 'route', route.id);
                                    }}
                                />
                            )}

                            {/* Pre-Snap Motion Path */}
                            {preSnapPoly && (
                                <path
                                    d={pointsToPath(preSnapPoly)}
                                    stroke={color}
                                    strokeWidth={effectivePrintMode ? (drawingSettings?.lineThickness || 0.3) * 1.5 : (drawingSettings?.lineThickness || 0.3)}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray="0.8, 1.2"
                                    opacity={opacity * 0.9}
                                    style={{
                                        display: ((playbackState.isPlaying || playbackState.progress > 0) && owner.hideRouteDuringPlayback) ? 'none' : 'block',
                                        pointerEvents: 'none'
                                    }}
                                />
                            )}

                            {/* SNAP Label Indicator */}
                            {preSnapPoly && preSnapPoly.length > 0 && !playbackState.isPlaying && (
                                <g pointerEvents="none">
                                    <text
                                        x={preSnapPoly[preSnapPoly.length - 1].x}
                                        y={preSnapPoly[preSnapPoly.length - 1].y}
                                        dy="-1.5"
                                        textAnchor="middle"
                                        fontSize="1.5"
                                        fontWeight="bold"
                                        fill={color}
                                        opacity={0.8}
                                        style={{ display: ((playbackState.isPlaying || playbackState.progress > 0) && owner.hideRouteDuringPlayback) ? 'none' : 'block' }}
                                    >
                                        SNAP
                                    </text>
                                </g>
                            )}

                            {postSnapPoly && (
                                <path
                                    d={pointsToPath(postSnapPoly)}
                                    stroke={color}
                                    strokeWidth={effectivePrintMode ? (drawingSettings?.lineThickness || 0.3) * 1.5 : (drawingSettings?.lineThickness || 0.3)}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray={dashArray}
                                    opacity={opacity}
                                    style={{
                                        display: ((playbackState.isPlaying || playbackState.progress > 0) && owner.hideRouteDuringPlayback) ? 'none' : 'block',
                                        pointerEvents: 'none'
                                    }}
                                    markerEnd={marker}
                                />
                            )}
                            {/* Handles (When selected or in select tool - only for straight lines) */}
                            {selectedTool === 'select' && !globalSettings.walkthroughMode && (selectedPlayerId === route.playerId) && route.isStraight && poly.map((pt, idx) => (
                                <g key={`handle-group-${route.id}-${idx}`}>
                                    {/* Larger invisible hitbox for handles */}
                                    <circle
                                        cx={`${pt.x}%`}
                                        cy={`${pt.y}%`}
                                        r="2.5"
                                        fill="transparent"
                                        style={{ cursor: 'move', pointerEvents: 'all' }}
                                        onMouseDown={(e) => {
                                            if (globalSettings.walkthroughMode) return; // Read-only routes
                                            e.stopPropagation();
                                            setDraggingPoint({ routeId: route.id, index: idx });
                                        }}
                                        onContextMenu={(e) => {
                                            if (effectivePrintMode) return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSelectedPlayerId?.(route.playerId);
                                            onContextMenu?.(e, idx === poly.length - 1 ? 'route-endpoint' : 'route', route.id);
                                        }}
                                    />
                                    <circle
                                        cx={`${pt.x}%`}
                                        cy={`${pt.y}%`}
                                        r="1.0"
                                        fill={color}
                                        stroke="white"
                                        strokeWidth="0.2"
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </g>
                            ))}
                        </g>
                    );
                })}

                {/* live preview while drawing (ABSOLUTE) */}
                {(currentAbs.length > 0) && (
                    <g>
                        <path
                            d={pointsToPath(potentialNextPoint ? [...currentAbs, potentialNextPoint] : currentAbs)}
                            stroke={drawingSettings?.color || 'yellow'}
                            strokeWidth={drawingSettings?.lineThickness || 0.3}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray={drawingSettings?.type === 'option' ? "0.1, 2.5" : "none"}
                        />
                        {(() => {
                            const last = potentialNextPoint || currentAbs[currentAbs.length - 1];
                            const first = currentAbs[0];
                            if (!first || !last) return null;
                            const yDist = Math.abs(last.y - first.y) * unitY_to_yd;

                            return (
                                <g pointerEvents="none">
                                    <text
                                        x={`${last.x + 2}%`}
                                        y={`${last.y}%`}
                                        fill={drawingSettings?.color || 'yellow'}
                                        fontSize="3"
                                        fontWeight="semibold"
                                        stroke="rgba(0,0,0,0.8)"
                                        strokeWidth="0.1"
                                        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,1))' }}
                                    >
                                        {yDist.toFixed(1)} yds
                                    </text>
                                </g>
                            );
                        })()}
                    </g>
                )}

                {/* Pending Start Point Indicator (Option Route UX) */}
                {selectedTool === 'draw' && drawingSettings?.startPos && !isDrawing && (
                    <g transform={`translate(${drawingSettings.startPos.x}, ${drawingSettings.startPos.y})`}>
                        <circle r="1" fill={drawingSettings.color || 'yellow'} opacity="0.6">
                            <animate attributeName="r" values="0.8;1.2;0.8" dur="1.5s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                        <circle r="2.5" fill="none" stroke={drawingSettings.color || 'yellow'} strokeWidth="0.2" opacity="0.4">
                            <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
                        </circle>
                    </g>
                )}





                {/* Blitz Distance Tooltip during drag */}
                {draggingId && (() => {
                    const draggingPlayer = players.find(p => p.id === draggingId);
                    if (!draggingPlayer || draggingPlayer.type !== 'blitz') return null;

                    const losMarker = players.find(p => p.type === 'los');
                    if (!losMarker) return null;

                    const distY = Math.abs(draggingPlayer.y - losMarker.y) * unitY_to_yd;

                    return (
                        <g pointerEvents="none">
                            <text
                                x={`${draggingPlayer.x + 2}%`}
                                y={`${draggingPlayer.y - 1}%`}
                                fill="#ef4444"
                                fontSize="3"
                                fontWeight="bold"
                                stroke="black"
                                strokeWidth="0.1"
                                textAnchor="start"
                            >
                                {distY.toFixed(1)} yds
                            </text>
                        </g>
                    );
                })()}

                {/* Wristband Guide Box */}
                {wristbandGuide.enabled && !effectivePrintMode && (
                    <g
                        onMouseDown={handleGuideDragStart}
                        style={{ cursor: 'move' }}
                    >
                        {/* Overlay to dim outside */}
                        <path
                            d={`M 0 0 H 100 V 100 H 0 Z M ${wristbandGuide.x - wristbandGuide.w / 2} ${wristbandGuide.y - wristbandGuide.h / 2} v ${wristbandGuide.h} h ${wristbandGuide.w} v -${wristbandGuide.h} z`}
                            fill="rgba(0,0,0,0.4)"
                            fillRule="evenodd"
                        />
                        {/* The Box itself */}
                        <rect
                            x={wristbandGuide.x - wristbandGuide.w / 2}
                            y={wristbandGuide.y - wristbandGuide.h / 2}
                            width={wristbandGuide.w}
                            height={wristbandGuide.h}
                            fill="transparent"
                            stroke="#3b82f6"
                            strokeWidth="0.4"
                            strokeDasharray="1,1"
                        />
                        {/* Label */}
                        <text
                            x={wristbandGuide.x - wristbandGuide.w / 2}
                            y={wristbandGuide.y - wristbandGuide.h / 2 - 1}
                            fill="#3b82f6"
                            fontSize="2"
                            fontWeight="bold"
                            style={{ pointerEvents: 'none' }}
                        >
                            WRISTBAND VIEW AREA
                        </text>
                    </g>
                )}

                {/* PLAYERS & ZONES */}
                {(() => {
                    // Ball is added via "Add Ball" tool - no forced injection
                    return [...players]
                        .filter(p => {
                            if (effectivePrintMode && printFilter === 'offense') {
                                return p.type === 'offense';
                            }
                            return true;
                        })
                        .sort((a, b) => {
                            const zA = a.type === 'ball' ? 1000 : (a.type === 'defense' ? 10 : 0);
                            const zB = b.type === 'ball' ? 1000 : (b.type === 'defense' ? 10 : 0);
                            return zA - zB;
                        }).map(p => {
                            const isSelected = selectedPlayerIds.has(p.id);
                            const isPrimarySelected = !effectivePrintMode && selectedPlayerId === p.id;
                            const isHovered = !effectivePrintMode && (hoveredPlayerId === p.id);

                            const showZone = !effectivePrintMode && p.zoneParams?.enabled;

                            // Global iconSize determines scale. Base radius approx 2.2 units
                            const sizeScale = (effectiveIconSize || 1.0) * (p.type === 'blitz' ? 0.3 : (p.type === 'cone' ? 0.24 : (p.type === 'firstdown' ? 0.8 : (p.type === 'line-marker' ? 0.4 : (p.type === 'los' ? 0.5 : 1.0)))));

                            // Use animated position if available, else static
                            const pos = displayPosById[p.id] || p;

                            return (
                                <g
                                    key={p.id}
                                    transform={`translate(${pos.x}, ${pos.y}) scale(${playerScale.x}, ${playerScale.y})`}
                                    onMouseDown={(e) => !effectivePrintMode && onMouseDownPlayer(e, p.id)}
                                    onMouseEnter={() => !effectivePrintMode && !isPanning && setHoveredPlayerId(p.id)}
                                    onMouseLeave={() => !effectivePrintMode && setHoveredPlayerId(null)}
                                    onClick={(e) => !effectivePrintMode && onClickPlayer(e, p.id)}
                                    onContextMenu={(e) => {
                                        if (effectivePrintMode) return;

                                        // SMART RIGHT CLICK for Walkthrough Mode
                                        if (globalSettings.walkthroughMode) {
                                            const now = Date.now();
                                            const start = markupStartRef.current;
                                            if (start) {
                                                const elapsed = now - start.time;
                                                const dist = Math.sqrt(Math.pow(e.clientX - start.x, 2) + Math.pow(e.clientY - start.y, 2));
                                                if (dist > 10 || elapsed > 300 || start.moved) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    return;
                                                }
                                            }
                                        }

                                        e.preventDefault();
                                        e.stopPropagation();

                                        // Ensure selection on right click even in walkthrough
                                        setSelectedPlayerId?.(p.id);
                                        setSelectedPlayerIds?.(new Set([p.id]));

                                        if (['offense', 'defense', 'ball', 'draw'].includes(selectedTool)) {
                                            onContextMenu?.(e, 'exit-draw');
                                            return;
                                        }
                                        const box = e.currentTarget.getBoundingClientRect();
                                        onContextMenu?.(e, 'player', p.id, { x: box.right + 10, y: box.top });
                                    }}
                                    style={{
                                        cursor: selectedTool === 'ball' ? (ballWizardStep === 2 && p.id !== ballSpec.currentHolderId ? 'grab' : 'default') : 'grab',
                                        pointerEvents: (selectedTool === 'ball' && ballWizardStep === 1) ? 'none' : 'all',
                                        opacity: (globalSettings.walkthroughMode && !isSelected && !p.isWalkthrough) ? globalSettings.walkthroughOpacity : 1,
                                        transition: 'opacity 0.2s ease-in-out'
                                    }}
                                >

                                    {/* Selection Halo (Hidden in print) */}
                                    {!effectivePrintMode && (isSelected || isHovered) && (
                                        <circle r={3.0 * sizeScale} fill="none" stroke={isPrimarySelected ? "white" : "rgba(255,255,255,0.4)"} strokeWidth="0.3" opacity={0.8} />
                                    )}

                                    {/* Shape Rendering */}
                                    {(() => {
                                        if (p.type === 'annotation') {
                                            const lines = (p.label || '').split('\n');
                                            const paddingW = 2.0;
                                            const paddingH = 1.0;
                                            const fontSize = 2.2;
                                            const lineHeight = 3.0;

                                            // Estimate dimensions based on text length
                                            const maxLineLen = Math.max(...lines.map(l => l.length), 4);
                                            const rectW = maxLineLen * 1.2 + paddingW * 2;
                                            const rectH = lines.length * lineHeight + paddingH * 2;

                                            return (
                                                <g>
                                                    <rect
                                                        x={-rectW / 2}
                                                        y={-rectH / 2}
                                                        width={rectW}
                                                        height={rectH}
                                                        fill={p.color || '#334155'}
                                                        fillOpacity="0.85"
                                                        stroke={isSelected ? '#3b82f6' : 'white'}
                                                        strokeWidth={isSelected ? "0.6" : "0.3"}
                                                        rx="1"
                                                    />
                                                    {lines.map((line, i) => (
                                                        <text
                                                            key={i}
                                                            x="0"
                                                            y={(-rectH / 2) + paddingH + (i * lineHeight) + (lineHeight / 2)}
                                                            fontSize={fontSize}
                                                            fill="white"
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 'bold' }}
                                                        >
                                                            {line}
                                                        </text>
                                                    ))}
                                                </g>
                                            );
                                        }
                                        if (p.type === 'freedraw') {
                                            // Show a small helper icon when NOT playing so user can find/move/delete it
                                            return (
                                                <g>
                                                    {!playbackState.isPlaying && (
                                                        <>
                                                            <circle r="2" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.2" strokeDasharray="0.5,0.5" />
                                                            <text y="0.5" textAnchor="middle" fontSize="2" opacity="0.6">✏️</text>
                                                        </>
                                                    )}
                                                    <circle r="2" fill="transparent" />
                                                </g>
                                            );
                                        }
                                        const shape = p.shape || 'Circle';
                                        const baseR = 2.0 * sizeScale;
                                        let color = p.color || (p.type === 'offense' ? '#fbbf24' : (p.type === 'ball' ? '#fbbf24' : '#ef4444'));
                                        if (p.type === 'blitz') {
                                            return (
                                                <g>
                                                    {p.showBlitzLine && (
                                                        <line
                                                            x1={-pos.x / playerScale.x}
                                                            y1={0}
                                                            x2={(100 - pos.x) / playerScale.x}
                                                            y2={0}
                                                            stroke={p.color || "#ef4444"}
                                                            strokeWidth={0.2 / playerScale.x}
                                                            strokeDasharray={`${1 / playerScale.x},${1 / playerScale.x}`}
                                                            opacity="0.5"
                                                            pointerEvents="none"
                                                        />
                                                    )}
                                                    <circle r={baseR} fill={p.color || "#ef4444"} stroke={effectivePrintMode ? "none" : "white"} strokeWidth={effectivePrintMode ? "0" : "0.2"} />
                                                </g>
                                            );
                                        }
                                        if (p.type === 'firstdown') {
                                            return (
                                                <g>
                                                    {p.showBlitzLine && (
                                                        <line
                                                            x1={-pos.x / playerScale.x}
                                                            y1={0}
                                                            x2={(100 - pos.x) / playerScale.x}
                                                            y2={0}
                                                            stroke={p.color || "#fbbf24"}
                                                            strokeWidth={0.3 / playerScale.x}
                                                            opacity="0.8"
                                                            pointerEvents="none"
                                                        />
                                                    )}
                                                    <circle r={baseR} fill={p.color || "#fbbf24"} stroke={effectivePrintMode ? "none" : "black"} strokeWidth={effectivePrintMode ? "0" : "0.2"} />
                                                    <text y={baseR * 0.4} textAnchor="middle" fontSize={baseR * 1.2} fontWeight="bold" fill="black">1</text>
                                                </g>
                                            );
                                        }
                                        if (p.type === 'line-marker') {
                                            return (
                                                <g>
                                                    {p.showBlitzLine && (
                                                        <line
                                                            x1={-pos.x / playerScale.x}
                                                            y1={0}
                                                            x2={(100 - pos.x) / playerScale.x}
                                                            y2={0}
                                                            stroke={p.color || "#9ca3af"}
                                                            strokeWidth={0.2 / playerScale.x}
                                                            strokeDasharray={p.lineStyle === 'dotted' ? `${1 / playerScale.x},${1 / playerScale.x}` : "none"}
                                                            opacity="0.6"
                                                            pointerEvents="none"
                                                        />
                                                    )}
                                                    <rect x={-baseR} y={-baseR * 0.2} width={baseR * 2} height={baseR * 0.4} fill={p.color || "#9ca3af"} rx={baseR * 0.1} />
                                                </g>
                                            );
                                        }
                                        if (p.type === 'los') {
                                            return (
                                                <g>
                                                    <line
                                                        x1={-pos.x / playerScale.x}
                                                        y1={0}
                                                        x2={(100 - pos.x) / playerScale.x}
                                                        y2={0}
                                                        stroke={isPrintMode ? "black" : "white"}
                                                        strokeWidth={0.2 / playerScale.x}
                                                        strokeDasharray={`${1 / playerScale.x},${1 / playerScale.x}`}
                                                        opacity="0.6"
                                                        pointerEvents="none"
                                                    />
                                                    <rect x={-baseR * 2} y={-baseR * 0.5} width={baseR * 4} height={baseR} fill={isPrintMode ? "#000" : "#222"} stroke={isPrintMode ? "black" : "#444"} strokeWidth="0.1" rx="1" />
                                                    <text y={baseR * 0.2} textAnchor="middle" fontSize={baseR * 0.8} fontWeight="bold" fill="white">LOS</text>
                                                </g>
                                            );
                                        }
                                        if (p.type === 'cone') {
                                            // Simple cone shape (triangle with flat base)
                                            return (
                                                <g>
                                                    <polygon points={`0,-${baseR * 1.5} -${baseR},${baseR} ${baseR},${baseR}`} fill="#fbbf24" stroke="#d97706" strokeWidth="0.2" />
                                                    <rect x={-baseR * 1.2} y={baseR} width={baseR * 2.4} height={baseR * 0.4} fill="#fbbf24" stroke="#d97706" strokeWidth="0.2" />
                                                </g>
                                            );
                                        }

                                        if (p.type === 'ball') {
                                            // Ball size (2x from 0.12 = 0.24)
                                            const ballScale = (sizeScale * 0.24) * (pos.scale || 1);
                                            // Only spin during throw (pos.isThrowing), not while carried
                                            const shouldSpin = pos.isThrowing && playbackState.isPlaying;
                                            return (
                                                <g>
                                                    {/* Hitbox */}
                                                    <circle r={1.5 * sizeScale} fill="transparent" />
                                                    <AutoBallSpiral
                                                        key={p.id}
                                                        scale={ballScale}
                                                        overrideHeading={pos.rotation || 0}
                                                        isPlaying={shouldSpin}
                                                        spinRps={4}
                                                    />
                                                </g>
                                            );
                                        }

                                        switch (shape) {
                                            case 'Square':
                                                const sH = baseR * 0.9;
                                                return <rect x={-sH} y={-sH} width={sH * 2} height={sH * 2} fill={color} stroke={isPrintMode ? "none" : (globalSettings.outerCircleMatchColor ? color : "#fff")} strokeWidth={isPrintMode ? "0" : "0.3"} rx="0.3" />;
                                            case 'Triangle':
                                                // Upward triangle
                                                const tR = baseR * 1.1;
                                                return <polygon points={`0,-${tR} -${tR},${tR * 0.8} ${tR},${tR * 0.8}`} fill={color} stroke={isPrintMode ? "none" : (globalSettings.outerCircleMatchColor ? color : "#fff")} strokeWidth={isPrintMode ? "0" : "0.3"} />;
                                            case 'X':
                                                const xS = baseR * 0.8;
                                                return (
                                                    <g stroke={color} strokeWidth={0.8 * sizeScale} strokeLinecap="round">
                                                        <line x1={-xS} y1={-xS} x2={xS} y2={xS} />
                                                        <line x1={xS} y1={-xS} x2={-xS} y2={xS} />
                                                    </g>
                                                );
                                            default:
                                                return <circle r={baseR} fill={color} stroke={isPrintMode ? "none" : (globalSettings.outerCircleMatchColor ? color : "#fff")} strokeWidth={isPrintMode ? "0" : "0.3"} />;
                                        }
                                    })()}

                                    {/* Label */}
                                    {p.type !== 'annotation' && p.type !== 'ball' && !globalSettings?.hidePlayerLabels && (
                                        <text
                                            y="0.5"
                                            fontSize={isPrintMode ? 2.0 * sizeScale : 1.4 * sizeScale}
                                            fontWeight={isPrintMode ? "black" : "bold"}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={p.type === 'offense' ? (isPrintMode ? '#000' : '#111') : 'white'}
                                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                                        >
                                            {(globalSettings?.showAsNumber && p.sequentialIndex) ? p.sequentialIndex : (() => {
                                                // 1. Hierarchical resolution based on mode
                                                let val = '';
                                                if (labelMode === 'user') val = p.customLabel;
                                                else if (labelMode === 'coaching') val = p.tag;
                                                else if (labelMode === 'numbers' && p.type === 'offense') {
                                                    if (p.role === 'C') return 'C';
                                                    if (p.role === 'QB') return 'QB';
                                                    const others = players
                                                        .filter(pl => pl.type === 'offense' && pl.role !== 'C' && pl.role !== 'QB')
                                                        .sort((a, b) => a.x - b.x);
                                                    const idx = others.findIndex(pl => pl.id === p.id);
                                                    if (idx !== -1) return (idx + 1).toString();
                                                }

                                                // If mode-specific value found, use it
                                                if (val) return val;

                                                // 2. Fallback to Role-based (Simple)
                                                if (p.role) {
                                                    if (p.role === 'DEF' && p.tag === 'R') return 'R';
                                                    return p.role;
                                                }

                                                // 3. Ultimate Fallback
                                                return p.label || '';
                                            })()}
                                        </text>
                                    )}
                                    {/* Full Name Label Below Icon (Hidden in Print offense filter) */}
                                    {!isPrintMode && globalSettings.showPlayerNames && p.name && (
                                        <text
                                            y={3.2 * sizeScale}
                                            fontSize={1.1 * sizeScale}
                                            fontWeight="normal"
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill="white"
                                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                                        >
                                            {p.name}
                                        </text>
                                    )}
                                </g>
                            );
                        });
                })()}

                {/* Wizard Step 3: Distance Preview (Ghost Line) - MOVED HERE so it appears ON TOP of players */}
                {selectedTool === 'ball' && ballWizardStep === 3 && ballHoverPos && ballSpec.currentHolderId && (() => {
                    const holderPos = displayPosById[ballSpec.currentHolderId]
                        || players.find(p => p.id === ballSpec.currentHolderId);

                    if (!holderPos) return null;

                    const dist = ydDistAbs(holderPos, ballHoverPos);

                    // Determine if we are hovering a player (handoff) or route (throw)
                    let isHoveringPlayer = false;
                    for (const pl of players) {
                        if (pl.id === ballSpec.currentHolderId) continue;
                        if (ydDistAbs(pl, ballHoverPos) < 3.0) {
                            isHoveringPlayer = true;
                            break;
                        }
                    }

                    const canThrow = !isHoveringPlayer && isNearAnyRoute(ballHoverPos);

                    // Only render the cursor overlay if we are actually hovering something meaningful
                    if (!isHoveringPlayer && !canThrow) return null;

                    const targetColor = isHoveringPlayer ? "#fbbf24" : "#10b981";

                    return (
                        <g pointerEvents="none">
                            {/* Dash line from holder to cursor */}
                            <line
                                x1={`${holderPos.x}%`} y1={`${holderPos.y}%`}
                                x2={`${ballHoverPos.x}%`} y2={`${ballHoverPos.y}%`}
                                stroke={targetColor}
                                strokeWidth="0.2"
                                strokeDasharray="1,1"
                                opacity="0.8"
                            />

                            {/* Icons following mouse */}
                            <g transform={`translate(${ballHoverPos.x}, ${ballHoverPos.y}) scale(${playerScale.x * 0.15}, ${playerScale.y * 0.15})`}>
                                <g className="animate-pulse" color={targetColor}>
                                    {isHoveringPlayer ? (
                                        <HandIcon scale={0.9} />
                                    ) : (
                                        <ReticleIcon scale={1.8} />
                                    )}
                                </g>
                                {isHoveringPlayer && (
                                    <circle r="12" fill="none" stroke="#fbbf24" strokeWidth="0.5" opacity="0.4">
                                        <animate attributeName="r" from="8" to="24" dur="1s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" from="0.6" to="0" dur="1s" repeatCount="indefinite" />
                                    </circle>
                                )}
                            </g>

                            {/* Distance Text */}
                            <text
                                x={`${ballHoverPos.x + 2}%`}
                                y={`${ballHoverPos.y - 1}%`}
                                fill={targetColor}
                                fontSize="2.2"
                                fontWeight="black"
                                stroke="black"
                                strokeWidth="0.1"
                                className="drop-shadow-md"
                            >
                                {dist.toFixed(1)} yds
                            </text>

                        </g>
                    );
                })()}
                {/* DRAW PREVIEW (Precision Draw Assistant) */}
                {isDrawing && (
                    <g pointerEvents="none">
                        {/* Finalized path segments so far */}
                        {currentAbs.length > 1 && (
                            <path
                                d={pointsToPath(currentAbs)}
                                stroke="white"
                                strokeWidth="0.2"
                                strokeDasharray="1,1"
                                fill="none"
                                opacity="0.4"
                            />
                        )}

                        {/* Angle Guides for Precision Draw */}
                        {straightLineMode && currentAbs.length > 0 && (
                            <g opacity="0.15">
                                {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
                                    const rad = angle * Math.PI / 180;
                                    const last = currentAbs[currentAbs.length - 1];
                                    return (
                                        <line
                                            key={angle}
                                            x1={last.x}
                                            y1={last.y}
                                            x2={last.x + Math.cos(rad) * 100}
                                            y2={last.y + Math.sin(rad) * 100}
                                            stroke="white"
                                            strokeWidth="0.05"
                                        />
                                    );
                                })}
                            </g>
                        )}

                        {/* Active segment (to potentialNextPoint) */}
                        {currentAbs.length > 0 && potentialNextPoint && (
                            <g>
                                <line
                                    x1={currentAbs[currentAbs.length - 1].x}
                                    y1={currentAbs[currentAbs.length - 1].y}
                                    x2={potentialNextPoint.x}
                                    y2={potentialNextPoint.y}
                                    stroke="white"
                                    strokeWidth="0.3"
                                    opacity="0.8"
                                />
                                {straightLineMode && (
                                    <circle
                                        cx={potentialNextPoint.x}
                                        cy={potentialNextPoint.y}
                                        r="0.4"
                                        fill="white"
                                        opacity="1"
                                    />
                                )}
                            </g>
                        )}
                    </g>
                )}

                {/* Selection Box (Hidden in print) */}
                {!isPrintMode && selectionBox && (
                    <rect
                        x={Math.min(selectionBox.start.x, selectionBox.current.x)}
                        y={Math.min(selectionBox.start.y, selectionBox.current.y)}
                        width={Math.abs(selectionBox.current.x - selectionBox.start.x)}
                        height={Math.abs(selectionBox.current.y - selectionBox.start.y)}
                        fill="rgba(59, 130, 246, 0.1)"
                        stroke="#3b82f6"
                        strokeWidth="0.2"
                        strokeDasharray="0.5,0.5"
                    />
                )}
                {/* Wristband Guide overlay */}
                {wristbandGuide.enabled && !isPrintMode && (
                    <g className="wristband-guide-overlay" onMouseDown={handleGuideDragStart} style={{ cursor: 'move' }}>
                        {/* Dimmed background around the guide */}
                        <path
                            d={`M 0,0 L 100,0 L 100,100 L 0,100 Z M ${wristbandGuide.x - wristbandGuide.w / 2},${wristbandGuide.y - wristbandGuide.h / 2} L ${wristbandGuide.x + wristbandGuide.w / 2},${wristbandGuide.y - wristbandGuide.h / 2} L ${wristbandGuide.x + wristbandGuide.w / 2},${wristbandGuide.y + wristbandGuide.h / 2} L ${wristbandGuide.x - wristbandGuide.w / 2},${wristbandGuide.y + wristbandGuide.h / 2} Z`}
                            fill="rgba(0,0,0,0.4)"
                            fillRule="evenodd"
                        />
                        {/* The guide box itself */}
                        <rect
                            x={wristbandGuide.x - wristbandGuide.w / 2}
                            y={wristbandGuide.y - wristbandGuide.h / 2}
                            width={wristbandGuide.w}
                            height={wristbandGuide.h}
                            fill="transparent"
                            stroke="#3b82f6"
                            strokeWidth="0.4"
                            strokeDasharray="1,1"
                        />
                        {/* Label */}
                        <text
                            x={wristbandGuide.x}
                            y={wristbandGuide.y - wristbandGuide.h / 2 - 1}
                            fill="#3b82f6"
                            fontSize="1.5"
                            fontWeight="bold"
                            textAnchor="middle"
                        >
                            WRISTBAND VIEW AREA
                        </text>
                    </g>
                )}
                {/* WALKTHROUGH MARKUP (TELESTRATOR) */}
                {markupLines.map(line => {
                    const age = Date.now() - line.ts;
                    const opacity = age < 3000 ? 1 : Math.max(0, 1 - (age - 3000) / 2000);
                    return (
                        <polyline
                            key={line.id}
                            points={line.points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="none"
                            stroke="#fbbf24" // Amber-400
                            strokeWidth="0.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity={opacity}
                            style={{ transition: 'opacity 0.2s linear' }}
                        />
                    );
                })}
                {currentMarkup && (
                    <polyline
                        points={currentMarkup.points.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke="#fbbf24"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                )}
            </svg>
        </div >
    );
};


export default Field;
